import { useState, useEffect } from 'react';
import { api } from '../../services/api';
import type { LLMProviderSettings, ApiFormat } from '../../services/types';

interface ProviderEditorModalProps {
  isOpen: boolean;
  provider: LLMProviderSettings | null;
  onClose: () => void;
  onSave: () => void;
}

export function ProviderEditorModal({
  isOpen,
  provider,
  onClose,
  onSave
}: ProviderEditorModalProps) {
  const isEditing = !!provider;

  const [providerId, setProviderId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [apiFormat] = useState<ApiFormat>('anthropic');
  const [baseUrl, setBaseUrl] = useState('');
  const [defaultModel, setDefaultModel] = useState('');
  const [priority, setPriority] = useState(100);
  const [extraConfig, setExtraConfig] = useState('');
  const [secretName, setSecretName] = useState('');
  const [isEnabled, setIsEnabled] = useState(false);

  const [isSaving, setIsSaving] = useState(false);
  const [isTogglingEnabled, setIsTogglingEnabled] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setError(null);
      if (provider) {
        // Editing mode
        setProviderId(provider.provider_id);
        setDisplayName(provider.display_name);
        setBaseUrl(provider.base_url);
        setDefaultModel(provider.default_model);
        setPriority(provider.priority);
        setExtraConfig(provider.extra_config || '');
        setSecretName(provider.secret_name || '');
        setIsEnabled(!!provider.is_enabled);
      } else {
        // Add mode - reset to defaults
        setProviderId('');
        setDisplayName('');
        setBaseUrl('');
        setDefaultModel('');
        setPriority(100);
        setExtraConfig('');
        setSecretName('');
        setIsEnabled(false);
      }
    }
  }, [isOpen, provider]);

  // Toggle enabled status
  const handleToggleEnabled = async () => {
    if (!provider?.id) return;

    try {
      setIsTogglingEnabled(true);
      setError(null);

      if (isEnabled) {
        await api.disableLLMProvider(provider.id);
        setIsEnabled(false);
      } else {
        await api.enableLLMProvider(provider.id);
        setIsEnabled(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle enabled status');
    } finally {
      setIsTogglingEnabled(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!providerId.trim()) {
      setError('Provider ID 不能為空');
      return;
    }
    if (!displayName.trim()) {
      setError('顯示名稱不能為空');
      return;
    }
    if (!baseUrl.trim()) {
      setError('Base URL 不能為空');
      return;
    }
    if (!defaultModel.trim()) {
      setError('預設模型不能為空');
      return;
    }
    if (!secretName.trim()) {
      setError('Secret 名稱不能為空');
      return;
    }

    // Parse extra config if provided
    let parsedExtraConfig: Record<string, unknown> | undefined;
    if (extraConfig.trim()) {
      try {
        parsedExtraConfig = JSON.parse(extraConfig);
      } catch {
        setError('Extra Config 必須是有效的 JSON');
        return;
      }
    }

    try {
      setIsSaving(true);
      setError(null);

      const data = {
        provider_id: providerId.trim(),
        display_name: displayName.trim(),
        api_format: apiFormat,
        base_url: baseUrl.trim(),
        default_model: defaultModel.trim(),
        priority,
        extra_config: parsedExtraConfig,
        secret_name: secretName.trim()
      };

      if (isEditing && provider) {
        await api.updateLLMProvider(provider.id, data);
      } else {
        await api.createLLMProvider(data);
      }

      onSave();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save provider');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal">
      {/* Overlay */}
      <div className="overlay" onClick={onClose} />

      {/* Modal Content */}
      <div className="modal-content max-w-lg">
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div className="card-header flex items-center justify-between">
            <h3 className="heading-section text-neutral-900 dark:text-neutral-50">
              {isEditing ? '編輯 Provider' : '新增 Provider'}
            </h3>
            <button
              type="button"
              onClick={onClose}
              className="btn btn-ghost btn-icon text-neutral-400 hover:text-neutral-600"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="card-body space-y-4 max-h-[60vh] overflow-y-auto">
            {/* Error */}
            {error && (
              <div className="p-3 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800/50 text-danger-700 dark:text-danger-300 rounded-button text-sm">
                {error}
              </div>
            )}

            {/* Enable/Disable Toggle - Only show when editing */}
            {isEditing && (
              <div className="flex items-center justify-between p-3 bg-neutral-50 dark:bg-neutral-800/50 rounded-button">
                <div>
                  <label className="label text-neutral-700 dark:text-neutral-300 block">
                    啟用狀態
                  </label>
                  <p className="text-xs text-neutral-500 mt-0.5">
                    {isEnabled ? '此 Provider 已啟用，可被系統使用' : '此 Provider 已停用，不會被系統使用'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleToggleEnabled}
                  disabled={isTogglingEnabled}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isEnabled
                      ? 'bg-primary-500'
                      : 'bg-neutral-300 dark:bg-neutral-600'
                    } ${isTogglingEnabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                  />
                </button>
              </div>
            )}

            {/* Provider ID */}
            <div>
              <label className="label text-neutral-600 dark:text-neutral-400 mb-1.5 block">
                Provider ID *
              </label>
              <input
                type="text"
                value={providerId}
                onChange={(e) => setProviderId(e.target.value)}
                placeholder="e.g., kimi-k2, minimax-anthropic"
                className="input w-full"
                disabled={isEditing}
              />
              <p className="text-xs text-neutral-500 mt-1">
                唯一識別碼，建議使用小寫英文與連字號
              </p>
            </div>

            {/* Display Name */}
            <div>
              <label className="label text-neutral-600 dark:text-neutral-400 mb-1.5 block">
                顯示名稱 *
              </label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g., Kimi K2 (月之暗面)"
                className="input w-full"
              />
            </div>

            {/* Secret Name */}
            <div>
              <label className="label text-neutral-600 dark:text-neutral-400 mb-1.5 block">
                Secret 名稱 *
              </label>
              <input
                type="text"
                value={secretName}
                onChange={(e) => setSecretName(e.target.value)}
                placeholder="e.g., KIMI_NOVEL_API_KEY"
                className="input w-full font-mono"
              />
              <p className="text-xs text-neutral-500 mt-1">
                Cloudflare Secret 的名稱，需在 Cloudflare Dashboard 或 CLI 設定對應的 API Key
              </p>
            </div>

            {/* API Format - Read only */}
            <div>
              <label className="label text-neutral-600 dark:text-neutral-400 mb-1.5 block">
                API 格式
              </label>
              <div className="input w-full bg-neutral-100 dark:bg-neutral-700 cursor-not-allowed">
                Anthropic Compatible
              </div>
              <p className="text-xs text-neutral-500 mt-1">
                使用 Anthropic SDK 格式（支援 Kimi K2、MiniMax、AGY Tools 等）
              </p>
            </div>

            {/* Base URL */}
            <div>
              <label className="label text-neutral-600 dark:text-neutral-400 mb-1.5 block">
                Base URL *
              </label>
              <input
                type="url"
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                placeholder="e.g., https://api.kimi.com/coding/v1"
                className="input w-full"
              />
            </div>

            {/* Default Model */}
            <div>
              <label className="label text-neutral-600 dark:text-neutral-400 mb-1.5 block">
                預設模型 *
              </label>
              <input
                type="text"
                value={defaultModel}
                onChange={(e) => setDefaultModel(e.target.value)}
                placeholder="e.g., kimi-for-coding, MiniMax-M2.1"
                className="input w-full"
              />
            </div>

            {/* Priority */}
            <div>
              <label className="label text-neutral-600 dark:text-neutral-400 mb-1.5 block">
                優先順序
              </label>
              <input
                type="number"
                value={priority}
                onChange={(e) => setPriority(parseInt(e.target.value) || 100)}
                className="input w-full"
                min={1}
                max={1000}
              />
              <p className="text-xs text-neutral-500 mt-1">
                數字越小優先順序越高
              </p>
            </div>

            {/* Extra Config */}
            <div>
              <label className="label text-neutral-600 dark:text-neutral-400 mb-1.5 block">
                額外設定 (JSON)
              </label>
              <textarea
                value={extraConfig}
                onChange={(e) => setExtraConfig(e.target.value)}
                placeholder='{"apiVersion": "2023-06-01"}'
                className="textarea w-full h-20 font-mono text-sm"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="card-footer flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-ghost btn-md"
              disabled={isSaving}
            >
              取消
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-md"
              disabled={isSaving}
            >
              {isSaving ? '儲存中...' : isEditing ? '更新' : '新增'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
